import { useState } from 'react';
import { REPLY_SENTIMENTS, type ReplySentiment } from '@epasu/core/constants';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';

export const SENTIMENT_LABEL: Record<ReplySentiment, string> = {
  positiv: 'Positiv',
  negativ: 'Abgesagt',
};

interface Props {
  /** Die eingegangene Antwort — nur an ihr steht die Bewertung. */
  messageId: string;
  value: ReplySentiment | null;
  onChanged: () => void;
  onError?: (text: string) => void;
}

/**
 * Positiv / Abgesagt an einer eingegangenen Antwort.
 *
 * Eine Komponente fuer Posteingang und Antwortliste: beide schreiben dieselbe
 * Spalte an derselben Nachricht, und was an der einen Stelle gedrueckt wurde,
 * steht an der anderen genauso. Die Bewertung zaehlt die Antwort zugleich in
 * der Kampagne, siehe `setReplySentiment` in core.
 */
export function ReplySentimentButtons({ messageId, value, onChanged, onError }: Props) {
  const [busy, setBusy] = useState(false);

  const bewerten = async (sentiment: ReplySentiment) => {
    setBusy(true);
    try {
      // Erneutes Klicken auf die schon aktive Bewertung nimmt sie zurück.
      await crm.mailbox.setReplySentiment(messageId, value === sentiment ? null : sentiment);
      onChanged();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Bewerten fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {REPLY_SENTIMENTS.map((s) => (
        <Button
          key={s}
          variant={value === s ? (s === 'positiv' ? 'success' : 'danger') : 'ghost'}
          size="sm"
          disabled={busy}
          aria-pressed={value === s}
          onClick={() => void bewerten(s)}
        >
          {s === 'positiv' ? (
            <ThumbsUp className="mr-1.5 h-4 w-4" />
          ) : (
            <ThumbsDown className="mr-1.5 h-4 w-4" />
          )}
          {SENTIMENT_LABEL[s]}
        </Button>
      ))}
    </>
  );
}
